import { useReadArticles } from "@serophin/nottldr-shared/hooks";
import { useEffect, useRef } from "preact/hooks";
import { useIsSignedIn } from "../../platform/session";
import type { CurrentArticle } from "../AudioPlayerContext";

const READ_FRACTION = 0.8;
const PREFETCH_SECONDS = 30;
const PREFETCH_ATTEMPTS = 3;

const prefetch = async (url: string): Promise<boolean> => {
  for (let attempt = 0; attempt < PREFETCH_ATTEMPTS; attempt++) {
    try {
      if ((await fetch(url)).ok) {
        return true;
      }
    } catch {
      // Tried again, then given up on: the track just loads when its turn comes.
    }
  }
  return false;
};

/**
 * What listening does besides playing, run on every time update: at 80 % a signed-in visitor's article is marked read, and in the last 30 seconds the next track is fetched so it starts without a wait.
 */
export const useListening = (current: CurrentArticle | null, nextUrl: () => string | null): ((audio: HTMLAudioElement) => void) => {
  const signedIn = useIsSignedIn();
  const { markRead } = useReadArticles();
  const marked = useRef(false);
  const prefetched = useRef<string | null>(null);
  const latest = useRef({ current, nextUrl, signedIn, markRead });
  latest.current = { current, nextUrl, signedIn, markRead };

  useEffect(() => {
    marked.current = false;
    prefetched.current = null;
  }, [current?.authorAlias, current?.articleId]);

  return (audio) => {
    const { current: article, nextUrl: next, signedIn: isSignedIn, markRead: mark } = latest.current;
    if (!article || !(audio.duration > 0)) {
      return;
    }
    if (!marked.current && audio.currentTime / audio.duration >= READ_FRACTION) {
      marked.current = true;
      if (isSignedIn) {
        mark(article.authorAlias, article.articleId).catch((error: unknown) => console.error("Failed to mark the article as read", error));
      }
    }
    const url = next();
    const remaining = audio.duration - audio.currentTime;
    if (url && url !== prefetched.current && remaining > 0 && remaining <= PREFETCH_SECONDS) {
      prefetched.current = url;
      void prefetch(url).then((ok) => {
        prefetched.current = ok ? url : null;
      });
    }
  };
};
